import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './utils/errorHandler';
import GlobalLoader from './components/GlobalLoader/GlobalLoader';

// Pages
import Landing from './pages/Landing/Landing';
import Login from './pages/Login/Login';
import ResetPasswordContainer from './pages/ResetPassword/ResetPasswordContainer';
import CreateAccount from './pages/CreateAccount/CreateAccount';
import TokenConfirmation from './pages/TokenConfirmation/TokenConfirmation';
import CompletePreRegister from './pages/CompletePreRegister/CompletePreRegister';
import PlanSelection from './pages/PlanSelection/PlanSelection';
import Dashboard from './apps/immigration-canada/pages/Dashboard/Dashboard';
import Dashboards from './apps/immigration-canada/pages/Dashboards/Dashboards';
import Features from './apps/immigration-canada/pages/Features/Features';
import Notifications from './apps/immigration-canada/pages/Notifications/Notifications';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import UserManagement from './apps/immigration-canada/pages/UserManagement/UserManagement';
import CustomerManagement from './apps/immigration-canada/pages/CustomerManagement/CustomerManagement';
import NotificationHistory from './apps/immigration-canada/pages/NotificationHistory/NotificationHistory';
import ImmigrationLanding from './apps/immigration-canada/pages/ImmigrationLanding/ImmigrationLanding';
import WarningEmailBlast from './apps/immigration-canada/pages/WarningEmailBlast/WarningEmailBlast';
import ExamPrepLanding from './apps/exam-prep/pages/ExamPrepLanding/ExamPrepLanding';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AccountSettings from './apps/immigration-canada/pages/AccountSettings/AccountSettings';
import SubscriptionPlan from './apps/immigration-canada/pages/_contexts/subscription/SubscriptionPlan/SubscriptionPlan';
import PaymentSuccess from './apps/immigration-canada/pages/_contexts/subscription/PaymentSuccess/PaymentSuccess';
import PaymentFailed from './apps/immigration-canada/pages/_contexts/subscription/PaymentFailed/PaymentFailed';
import Subscription from './pages/Subscription/Subscription';
import { ga4 } from './utils/analytics/ga4';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AnalyticsTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    ga4.init();
  }, []);

  useEffect(() => {
    const allowList = new Set([
      '/',
      '/imigra-pulse-canada',
      '/login',
      '/token-confirmation',
      '/confirmacao-token',
      '/plan-selection',
      '/selecao-plano',
      '/planos',
      '/plans',
    ]);

    if (!allowList.has(location.pathname)) return;

    const searchParams = new URLSearchParams(location.search || '');
    for (const key of Array.from(searchParams.keys())) {
      if (/(guid|token|email)/i.test(key)) {
        searchParams.delete(key);
      }
    }

    const sanitizedSearch = searchParams.toString();
    const path = sanitizedSearch ? `${location.pathname}?${sanitizedSearch}` : location.pathname;

    ga4.pageView(path);
  }, [location.pathname, location.search]);

  return null;
};

const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();

  // Mostrar loading apenas na primeira verificação de autenticação
  // Não mostrar loading durante navegação entre páginas públicas
  if (isLoading) {
    const currentPath = window.location.pathname;
    const isPublicRoute = ['/', '/imigra-pulse-canada', '/exam-prep', '/preparacao-concursos', '/login', '/reset-password', '/plan-selection', '/selecao-plano', '/planos', '/plans', '/error'].includes(currentPath);
    
    // Se estiver em uma rota pública, não mostrar loading
    if (isPublicRoute) {
      // Continue sem mostrar loading para rotas públicas
    } else {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px'
        }}>
          Carregando...
        </div>
      );
    }
  }

  return (
    <Routes>
      {/* Landing page - página inicial */}
      <Route path="/" element={<Landing />} />
      
      {/* Immigration Canada Landing */}
      <Route path="/imigra-pulse-canada" element={<ImmigrationLanding />} />
      
      {/* Exam Prep Landing */}
      <Route path="/exam-prep" element={<ExamPrepLanding />} />
      <Route path="/preparacao-concursos" element={<ExamPrepLanding />} />
      
      {/* Authentication routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPasswordContainer />} />
      <Route path="/create-account" element={<CreateAccount />} />
      <Route path="/criar-conta" element={<CreateAccount />} />
      <Route path="/completar-pre-cadastro/:preRegistrationGuid" element={<CompletePreRegister />} />
      <Route path="/token-confirmation" element={<TokenConfirmation />} />
      <Route path="/confirmacao-token" element={<TokenConfirmation />} />
      <Route path="/plan-selection" element={<PlanSelection />} />
      <Route path="/selecao-plano" element={<PlanSelection />} />
      <Route path="/planos" element={<PlanSelection />} />
      <Route path="/plans" element={<PlanSelection />} />

      {/* Error page */}
      <Route path="/error" element={<ErrorPage />} />

      {/* Protected routes - Immigration Canada App */}
      <Route
        path="/imigra-pulse-canada/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/painel"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/features"
        element={
          <ProtectedRoute>
            <Features />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/recursos"
        element={
          <ProtectedRoute>
            <Features />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/notificacoes"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/dashboards"
        element={
          <ProtectedRoute>
            <Dashboards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/dashboards"
        element={
          <ProtectedRoute>
            <Dashboards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/notification-history"
        element={
          <ProtectedRoute>
            <NotificationHistory />
          </ProtectedRoute>
        }
      />

      {/* Protected - Account Settings */}
      <Route
        path="/account-settings"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/configuracoes-da-conta"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription-plan"
        element={
          <ProtectedRoute>
            <SubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/assinatura-plano"
        element={
          <ProtectedRoute>
            <SubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/subscription-plan"
        element={
          <ProtectedRoute>
            <SubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/checkout-com-sucesso"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/checkout-nao-concluido"
        element={
          <ProtectedRoute>
            <PaymentFailed />
          </ProtectedRoute>
        }
      />
      <Route path="/imigra-pulse-canada/pagamento-sucesso" element={<Navigate to="/imigra-pulse-canada/checkout-com-sucesso" />} />
      <Route path="/imigra-pulse-canada/pagamento-nao-concluido" element={<Navigate to="/imigra-pulse-canada/checkout-nao-concluido" />} />
      <Route
        path="/subscription-plan"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assinatura-plano"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/historico-notificacoes"
        element={
          <ProtectedRoute>
            <NotificationHistory />
          </ProtectedRoute>
        }
      />

      {/* SuperUser only routes - Immigration Canada App */}
      <Route
        path="/imigra-pulse-canada/user-management"
        element={
          <ProtectedRoute requireSuperUser={true} allowAdmin={true}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/gerenciamento-usuarios"
        element={
          <ProtectedRoute requireSuperUser={true} allowAdmin={true}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/customer-management"
        element={
          <ProtectedRoute requireSuperUser={true}>
            <CustomerManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/gerenciamento-clientes"
        element={
          <ProtectedRoute requireSuperUser={true}>
            <CustomerManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/warning-email-blast"
        element={
          <ProtectedRoute requireSuperUser={true}>
            <WarningEmailBlast />
          </ProtectedRoute>
        }
      />
      <Route
        path="/imigra-pulse-canada/disparos-avisos"
        element={
          <ProtectedRoute requireSuperUser={true}>
            <WarningEmailBlast />
          </ProtectedRoute>
        }
      />

      {/* Development routes */}
      <Route path="/dev/dashboard" element={<Dashboard />} />
      <Route path="/dev/features" element={<Features />} />
      <Route path="/dev/notifications" element={<Notifications />} />
      {/* Removed Templates dev route */}

      {/* Legacy routes - redirect to new Imigra Pulse Canada paths */}
      <Route path="/dashboard" element={<Navigate to="/imigra-pulse-canada/dashboard" />} />
      <Route path="/features" element={<Navigate to="/imigra-pulse-canada/features" />} />
      <Route path="/notifications" element={<Navigate to="/imigra-pulse-canada/notifications" />} />
      {/* Removed legacy templates redirect */}
      <Route path="/notification-history" element={<Navigate to="/imigra-pulse-canada/notification-history" />} />
      <Route path="/user-management" element={<Navigate to="/imigra-pulse-canada/user-management" />} />
      <Route path="/customer-management" element={<Navigate to="/imigra-pulse-canada/customer-management" />} />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App: React.FC = () => {
  // Inicializar o tratamento global de erros
  useEffect(() => {
    // O globalErrorHandler já é inicializado automaticamente como singleton
    console.log('🛡️ Sistema de tratamento de erros inicializado');
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <AnalyticsTracker />
            <GlobalLoader />
            <AppRoutes />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
